// Which textures a material needs and how big they are: pure data, no THREE / canvas, shared by the
// main thread (cache, budgets) and the texture worker (generation).
//
// Colours are handled in one of two ways per texture kind:
//  - tinted kinds (plain, concrete, tiles) are painted once in neutral near-white and the material
//    colour supplies the hue, so every colour of such a surface shares one texture;
//  - baked kinds paint the colour into the tile, because the pattern has a substrate (bare steel
//    under paint, a dark partner stripe, glass tint) that a plain multiply cannot give.

import { parseHex, rgbToHex } from "./palette";
import type { CamoName, TextureKind } from "./types";

/** Grime is quantised to 0, 0.5 and 1: finer levels would only multiply the textures of a map. */
export const GRIME_LEVELS = 2;

/** Baked colours snap to a grid of this many 0..255 steps, so near-identical colours share a texture. */
const COLOR_STEP = 8;

/** Neutral paint of the tinted kinds. */
export const NEUTRAL_PAINT = "#ffffff";

/** Smallest edge a scaled texture shrinks to. */
export const MIN_TEXTURE_SIZE = 32;

/** Mean sRGB level (0..1) a tinted kind is painted at; `gain` brings the average back to the material colour. */
const TINTED_LEVEL: Partial<Record<TextureKind, number>> = {
  plain: 0.985,
  concrete: 0.94,
  tiles: 0.94,
};

/** Edge length (px) of each kind at full quality. */
export const SURFACE_SIZE: Record<TextureKind, number> = {
  plain: 256,
  concrete: 512,
  tiles: 512,
  panel: 512,
  metal: 256,
  grate: 256,
  stripes: 256,
  glass: 128,
  wood: 256,
  asphalt: 256,
};

/** Edge length (px) of the weapon camo textures at full quality. */
export const CAMO_SIZE = 256;

/** Edge length (px) of the static decal atlas at full quality. */
export const DECAL_ATLAS_SIZE = 512;

/** What the worker draws. */
export type TextureJob =
  | {
      readonly type: "surface";
      readonly kind: TextureKind;
      /** Colour painted into the tile (neutral for tinted kinds). */
      readonly paint: string;
      /** Quantised: 0, 0.5 or 1. */
      readonly grime: number;
      /** Cache key; seeds the generator so the same key always gives the same pixels. */
      readonly key: string;
      readonly size: number;
    }
  | { readonly type: "camo"; readonly camo: CamoName; readonly key: string; readonly size: number }
  | { readonly type: "decals"; readonly key: string; readonly size: number };

export interface WorkerRequest {
  readonly id: number;
  readonly job: TextureJob;
  /** Edge of the bitmap sent back; the worker scales the drawing down to it. */
  readonly outSize: number;
}

export type WorkerResponse =
  | { readonly id: number; readonly bitmap: ImageBitmap }
  | { readonly id: number; readonly error: string };

/** A surface texture request resolved to what is cached and how the material uses it. */
export interface SurfaceSpec {
  readonly job: Extract<TextureJob, { type: "surface" }>;
  /** Material colour multiplier ("#ffffff" when the colour is baked into the texture). */
  readonly tint: string;
  /** Linear multiplier on the material colour that restores the brightness a tinted texture loses to its own shading. */
  readonly gain: number;
}

/** Quantised grime (0, 0.5 or 1) of any input. */
export function grimeLevel(grime: number): number {
  const g = grime > 0 ? (grime < 1 ? grime : 1) : 0;
  return Math.round(g * GRIME_LEVELS) / GRIME_LEVELS;
}

/** Snaps every channel of "#rrggbb" to the colour grid. */
export function quantiseColor(color: string): string {
  const c = parseHex(color);
  const snap = (v: number): number => Math.min(255, Math.round(v / COLOR_STEP) * COLOR_STEP);
  return rgbToHex(snap(c.r), snap(c.g), snap(c.b));
}

function srgbToLinear(v: number): number {
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

/** Resolves a (kind, colour, grime) request to the shared texture it uses. */
export function surfaceSpec(kind: TextureKind, color: string, grime: number): SurfaceSpec {
  const level = grimeLevel(grime);
  const tintedLevel = TINTED_LEVEL[kind];
  const paint = tintedLevel === undefined ? quantiseColor(color) : NEUTRAL_PAINT;
  return {
    job: { type: "surface", kind, paint, grime: level, key: `${kind}|${paint}|${level}`, size: SURFACE_SIZE[kind] },
    tint: tintedLevel === undefined ? NEUTRAL_PAINT : color,
    gain: tintedLevel === undefined ? 1 : 1 / srgbToLinear(tintedLevel),
  };
}

/** Edge length after the quality scale (1 = full, 0.5 = half in each dimension). */
export function scaledSize(size: number, scale: number): number {
  return Math.max(MIN_TEXTURE_SIZE, Math.min(size, Math.round(size * scale)));
}

/** GPU bytes of an RGBA8 texture of this edge with a full mip chain. */
export function textureBytes(size: number): number {
  return Math.round(size * size * 4 * (4 / 3));
}

export interface MaterialTextureInput {
  readonly texture: TextureKind;
  readonly color: string;
  readonly grime: number;
}

export interface TexturePlan {
  /** Distinct textures the materials share. */
  readonly textures: number;
  readonly bytes: number;
}

/** Textures and GPU memory a set of map materials needs at a given quality scale. */
export function planTextures(materials: Iterable<MaterialTextureInput>, scale: number): TexturePlan {
  const seen = new Map<string, number>();
  for (const m of materials) {
    const { job } = surfaceSpec(m.texture, m.color, m.grime);
    seen.set(job.key, textureBytes(scaledSize(job.size, scale)));
  }
  let bytes = 0;
  for (const b of seen.values()) bytes += b;
  return { textures: seen.size, bytes };
}
